import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import NewsCard from "../cards/news/NewsCard";
import SEO from "../common/SEO";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";

const News = ({ news, formatDate }) => {
  const { i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");

  const isEnglish = (i18n.language || "it").startsWith("en");
  const lang = isEnglish ? "en" : "it";
  const title = isEnglish ? "News & Press Releases" : "Notizie & Comunicati Stampa";
  const description = isEnglish
    ? "Latest news, press releases, and announcements from the Nuova Orchestra da Camera Ferruccio Busoni."
    : "Tutte le ultime notizie, comunicati stampa e novità della Nuova Orchestra da Camera Ferruccio Busoni.";

  const filteredNews = useMemo(() => {
    if (!searchTerm.trim()) return news;
    const term = searchTerm.toLowerCase();
    return news.filter((item) => {
      const itemTitle = (lang === "en" ? item.title_en : item.title_it) || "";
      const itemBody = (lang === "en" ? item.body_en : item.body_it) || "";
      return (
        itemTitle.toLowerCase().includes(term) ||
        itemBody.toLowerCase().includes(term)
      );
    });
  }, [news, searchTerm, lang]);

  return (
    <>
      <SEO title={title} description={description} />
      <section className="section subpage-section">
        <div className="container">
          {/* Section Header */}
          <div className="section-title text-center mb-4">
            <h1 className="uppercasebold1">{title}</h1>
            <p className="subpage-subtitle text-muted mt-2">
              {isEnglish
                ? "Official announcements, press reviews and artistic updates"
                : "Comunicati ufficiali, rassegna stampa e aggiornamenti artistici"}
            </p>
          </div>

          {/* Interactive Search Bar */}
          <div className="row justify-content-center mb-5">
            <div className="col-12 col-md-8 col-lg-6">
              <div className="modern-search-wrapper">
                <FontAwesomeIcon icon={faSearch} className="modern-search-icon" />
                <input
                  type="text"
                  className="form-control modern-search-input"
                  placeholder={
                    isEnglish
                      ? "Search articles and news..."
                      : "Cerca articoli e notizie..."
                  }
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  aria-label="Cerca notizie"
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="modern-search-clear"
                    onClick={() => setSearchTerm("")}
                    aria-label="Cancella ricerca"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                )}
              </div>
              {searchTerm && (
                <div className="text-center mt-2 small text-muted">
                  {filteredNews.length}{" "}
                  {filteredNews.length === 1
                    ? isEnglish
                      ? "article found"
                      : "articolo trovato"
                    : isEnglish
                    ? "articles found"
                    : "articoli trovati"}
                </div>
              )}
            </div>
          </div>

          {/* News List */}
          <div className="row justify-content-center">
            <div className="col-12 col-lg-10">
              {filteredNews.length > 0 ? (
                filteredNews.map((newsItem) => (
                  <NewsCard
                    key={newsItem.id}
                    newsItem={newsItem}
                    formatDate={formatDate}
                  />
                ))
              ) : (
                <div className="text-center py-5 no-results-box">
                  <p className="text-muted mb-3">
                    {isEnglish
                      ? "No articles found matching your search."
                      : "Nessun articolo trovato con questi criteri di ricerca."}
                  </p>
                  <button
                    type="button"
                    className="btn btn-outline-warning btn-sm"
                    onClick={() => setSearchTerm("")}
                  >
                    {isEnglish ? "Clear Search" : "Mostra tutti gli articoli"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default News;
